import { AxisBottom, AxisLeft } from '@visx/axis'
import { Group } from '@visx/group'
import { scaleBand, scaleLinear } from '@visx/scale'
import { Bar, LinePath, Pie } from '@visx/shape'
import {
  base_colorContentBorder,
  text_colorTextSecondary,
} from 'ext-ui/style/colors'
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import * as tooltipStyle from 'src/charting/renderers/visx/VisxStackedBarChart.style'
import { useTranslate } from 'src/localization/Trns'

import type {
  ReportingChartPlan,
  ReportingCurrencyDisplay,
  ReportingRenderRow,
  ReportingRenderValue,
} from './reportingChartPlan'
import {
  chartColors,
  interactiveLinePoint,
  reportingTooltipSeriesList,
  reportingTooltipViewport,
} from './ReportingChartRenderer.style'

const HORIZONTAL_CATEGORY_AXIS_WIDTH = 140

const formatDate = (value: string, locale: string): string => {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

// IEEE-754 binary64 values round-trip with at most 17 significant digits.
const MAX_REPORTING_SIGNIFICANT_DIGITS = 17

const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    maximumSignificantDigits: MAX_REPORTING_SIGNIFICANT_DIGITS,
  }).format(value)

const percentFractionDigits = (value: number): number => {
  const [coefficient, exponentText = '0'] = Math.abs(value)
    .toString()
    .toLowerCase()
    .split('e')
  const coefficientFractionDigits =
    coefficient.split('.')[1]?.length ?? 0
  return Math.min(
    20,
    Math.max(0, coefficientFractionDigits - Number(exponentText)),
  )
}

const formatPercent = (value: number, locale: string): string => {
  const formattedValue = new Intl.NumberFormat(locale, {
    maximumFractionDigits: percentFractionDigits(value),
  }).format(value)
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .map((part) => (part.type === 'integer' ? formattedValue : part.value))
    .join('')
}

export const formatReportingValue = (
  value: ReportingRenderValue | undefined,
  format?: string,
  currency?: ReportingCurrencyDisplay,
  locale = 'en-US',
): string => {
  if (value === null || value === undefined) return 'null'
  if (format === 'date' && typeof value === 'string') {
    return formatDate(value, locale)
  }
  if (format === 'percent') {
    return typeof value === 'number'
      ? formatPercent(value, locale)
      : `${String(value)}%`
  }
  if (format === 'currency' && currency && typeof value === 'number') {
    const amount =
      currency.unit === 'minor_currency'
        ? value / currency.minorUnitScale
        : value
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.currencyCode,
    }).format(amount)
  }
  if (typeof value === 'number') return formatNumber(value, locale)
  return String(value)
}

const CHART_WIDTH = 640
const CHART_HEIGHT = 320
const CHART_MARGIN = { top: 20, right: 24, bottom: 44, left: 64 }

interface VisxBarMark {
  id: string
  rowIndex: number
  seriesKey: string
  x: number
  y: number
  width: number
  height: number
  color: string
}

interface ActiveTooltip {
  rowIndex: number
  seriesKey: string
  anchorX: number
  anchorY: number
}

const TOOLTIP_OFFSET = 16
const TOOLTIP_DEFAULT_WIDTH = 236
const TOOLTIP_INSET = 4
const TOOLTIP_CLOSE_DELAY_MS = 100

const numericPlanValue = (
  row: ReportingRenderRow,
  key: string,
): number | undefined => {
  const value = row[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const tooltipPosition = (
  active: ActiveTooltip,
  size: { width: number; height: number },
): { left: number; top: number } => {
  const viewportWidth =
    typeof window === 'undefined' ? CHART_WIDTH : window.innerWidth
  const viewportHeight =
    typeof window === 'undefined' ? CHART_HEIGHT : window.innerHeight
  const tooltipWidth = Math.max(size.width, 1)
  const right = active.anchorX + TOOLTIP_OFFSET
  const left =
    right + tooltipWidth + TOOLTIP_INSET <= viewportWidth
      ? right
      : active.anchorX - TOOLTIP_OFFSET - tooltipWidth
  return {
    left: clamp(
      left,
      TOOLTIP_INSET,
      Math.max(TOOLTIP_INSET, viewportWidth - tooltipWidth - TOOLTIP_INSET),
    ),
    top: clamp(
      active.anchorY + TOOLTIP_OFFSET,
      TOOLTIP_INSET,
      Math.max(TOOLTIP_INSET, viewportHeight - size.height - TOOLTIP_INSET),
    ),
  }
}

const useReportingChartTooltip = (
  plan: ReportingChartPlan,
  locale: string,
) => {
  const [active, setActive] = useState<ActiveTooltip | null>(null)
  const [tooltipSize, setTooltipSize] = useState({
    width: TOOLTIP_DEFAULT_WIDTH,
    height: 0,
  })
  const tooltipRef = useRef<HTMLDivElement>(null)
  const activeRowRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tooltipId = useId()
  const translate = useTranslate()

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current === null) return
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const close = useCallback(() => {
    cancelScheduledClose()
    setActive(null)
  }, [cancelScheduledClose])

  const scheduleClose = useCallback(() => {
    cancelScheduledClose()
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      setActive(null)
    }, TOOLTIP_CLOSE_DELAY_MS)
  }, [cancelScheduledClose])

  const openFromPointer = useCallback(
    (
      rowIndex: number,
      seriesKey: string,
      event: React.MouseEvent<SVGElement>,
    ) => {
      cancelScheduledClose()
      setActive({
        rowIndex,
        seriesKey,
        anchorX: event.clientX,
        anchorY: event.clientY,
      })
    },
    [cancelScheduledClose],
  )
  const openFromFocus = useCallback(
    (
      rowIndex: number,
      seriesKey: string,
      event: React.FocusEvent<SVGElement>,
    ) => {
      cancelScheduledClose()
      const bounds = event.currentTarget.getBoundingClientRect()
      setActive({
        rowIndex,
        seriesKey,
        anchorX: bounds.right,
        anchorY: bounds.top + bounds.height / 2,
      })
    },
    [cancelScheduledClose],
  )

  useEffect(() => cancelScheduledClose, [cancelScheduledClose])

  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined
    const handleScroll = (event: Event) => {
      const target = event.target
      if (target instanceof Node && tooltipRef.current?.contains(target)) {
        return
      }
      close()
    }
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [active, close])

  useLayoutEffect(() => {
    if (!active) return undefined
    const tooltipElement = tooltipRef.current
    if (!tooltipElement) return undefined

    const updateTooltipSize = () => {
      const bounds = tooltipElement.getBoundingClientRect()
      const nextSize = {
        width: Math.max(Math.round(bounds.width) || TOOLTIP_DEFAULT_WIDTH, 1),
        height: Math.max(Math.round(bounds.height) || 0, 0),
      }
      setTooltipSize((currentSize) =>
        currentSize.width === nextSize.width &&
        currentSize.height === nextSize.height
          ? currentSize
          : nextSize,
      )
    }

    updateTooltipSize()
    if (typeof ResizeObserver === 'undefined') return undefined

    const resizeObserver = new ResizeObserver(updateTooltipSize)
    resizeObserver.observe(tooltipElement)
    return () => resizeObserver.disconnect()
  }, [active])

  useLayoutEffect(() => {
    if (!active) return
    activeRowRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [active])

  const tooltip = useMemo(() => {
    if (!active) return null
    const row = plan.data[active.rowIndex]
    if (!row) return null
    const category = row[plan.categoryDataKey]
    const rows = plan.valueDataKeys.flatMap((key, index) => {
      const value = numericPlanValue(row, key)
      if (value === undefined) return []
      return [
        {
          key,
          label: plan.seriesLabels[key] ?? translate('Value'),
          value,
          color:
            plan.kind === 'pie'
              ? chartColors[active.rowIndex % chartColors.length]
              : chartColors[index % chartColors.length],
        },
      ]
    })
    return {
      title:
        plan.kind === 'line'
          ? formatDate(String(category ?? 'null'), locale)
          : String(category ?? 'null'),
      rows,
    }
  }, [active, locale, plan, translate])

  const position = useMemo(
    () => (active ? tooltipPosition(active, tooltipSize) : null),
    [active, tooltipSize],
  )

  const renderedTooltip =
    active && tooltip && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            css={[tooltipStyle.tooltip, reportingTooltipViewport]}
            role="tooltip"
            data-testid="reporting-chart-tooltip"
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={scheduleClose}
            style={
              {
                '--tooltip-x': `${position?.left ?? 0}px`,
                '--tooltip-y': `${position?.top ?? 0}px`,
              } as React.CSSProperties
            }
          >
            <p css={tooltipStyle.tooltipTitle}>{tooltip.title}</p>
            <div
              css={[tooltipStyle.tooltipTable, reportingTooltipSeriesList]}
              role="table"
            >
              {tooltip.rows.map((row) => {
                const isActive = row.key === active.seriesKey
                return (
                  <div
                    key={row.key}
                    ref={isActive ? activeRowRef : undefined}
                    css={tooltipStyle.tooltipSegmentRow(isActive)}
                    role="row"
                    data-active={isActive ? 'true' : 'false'}
                  >
                    <span css={tooltipStyle.tooltipSeriesCell} role="cell">
                      <span
                        css={tooltipStyle.tooltipSwatch}
                        style={{ backgroundColor: row.color }}
                        aria-hidden="true"
                      />
                      {row.label}
                    </span>
                    <span css={tooltipStyle.tooltipValue} role="cell">
                      {formatReportingValue(
                        row.value,
                        plan.valueFormat,
                        plan.valueCurrency,
                        locale,
                      )}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>,
          document.body,
        )
      : null

  return {
    active,
    close,
    scheduleClose,
    openFromFocus,
    openFromPointer,
    renderedTooltip,
    tooltipId,
  }
}

const VisxPieChart: React.FC<{
  plan: ReportingChartPlan
  locale: string
}> = ({ plan, locale }) => {
  const pieData = plan.data.filter(
    (row) => numericPlanValue(row, plan.valueDataKeys[0]) !== undefined,
  )
  const radius = 118
  const tooltip = useReportingChartTooltip(plan, locale)
  return (
    <>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        data-visx-renderer="pie"
      >
        <Group top={CHART_HEIGHT / 2} left={CHART_WIDTH / 2}>
          <Pie<ReportingRenderRow>
            data={pieData}
            pieValue={(row) =>
              numericPlanValue(row, plan.valueDataKeys[0]) ?? 0
            }
            outerRadius={radius}
            innerRadius={0}
            padAngle={0.015}
          >
            {(pie) =>
              pie.arcs.map((arc, index) => {
                const category = String(arc.data[plan.categoryDataKey] ?? '')
                return (
                  <path
                    key={`${category}-${index}`}
                    d={pie.path(arc) ?? undefined}
                    fill={chartColors[index % chartColors.length]}
                    data-visx-mark="pie-slice"
                    tabIndex={0}
                    role="graphics-symbol"
                    aria-label={`${category}: ${formatReportingValue(
                      numericPlanValue(arc.data, plan.valueDataKeys[0]),
                      plan.valueFormat,
                      plan.valueCurrency,
                      locale,
                    )}`}
                    aria-describedby={
                      tooltip.active?.rowIndex === index
                        ? tooltip.tooltipId
                        : undefined
                    }
                    css={tooltipStyle.barSegment}
                    onMouseEnter={(event) =>
                      tooltip.openFromPointer(
                        index,
                        plan.valueDataKeys[0],
                        event,
                      )
                    }
                    onMouseMove={(event) =>
                      tooltip.openFromPointer(
                        index,
                        plan.valueDataKeys[0],
                        event,
                      )
                    }
                    onMouseLeave={tooltip.scheduleClose}
                    onFocus={(event) =>
                      tooltip.openFromFocus(index, plan.valueDataKeys[0], event)
                    }
                    onBlur={tooltip.close}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') tooltip.close()
                    }}
                  />
                )
              })
            }
          </Pie>
        </Group>
      </svg>
      {tooltip.renderedTooltip}
    </>
  )
}

const VisxCartesianChart: React.FC<{
  plan: ReportingChartPlan
  locale: string
}> = ({ plan, locale }) => {
  const tooltip = useReportingChartTooltip(plan, locale)
  const translate = useTranslate()
  const horizontal = plan.categoryAxis === 'y'
  const categories = plan.data.map((row) =>
    String(row[plan.categoryDataKey] ?? 'null'),
  )
  const isStacked = plan.kind === 'stacked_bar'
  const individualValues = plan.data.flatMap((row) =>
    plan.valueDataKeys
      .map((key) => numericPlanValue(row, key))
      .filter((value): value is number => value !== undefined),
  )
  const stackedExtents = plan.data.flatMap((row) => {
    let positive = 0
    let negative = 0
    plan.valueDataKeys.forEach((key) => {
      const value = numericPlanValue(row, key) ?? 0
      if (value >= 0) positive += value
      else negative += value
    })
    return [negative, positive]
  })
  const domainValues = isStacked ? stackedExtents : individualValues
  const domainMin = Math.min(0, ...domainValues)
  const domainMax = Math.max(0, ...domainValues)
  const safeDomain: [number, number] =
    domainMin === domainMax ? [0, 1] : [domainMin, domainMax]
  const margin = horizontal
    ? { ...CHART_MARGIN, left: HORIZONTAL_CATEGORY_AXIS_WIDTH }
    : CHART_MARGIN
  const innerWidth = CHART_WIDTH - margin.left - margin.right
  const innerHeight = CHART_HEIGHT - margin.top - margin.bottom
  const categoryScale = scaleBand<string>({
    domain: categories,
    range: horizontal ? [0, innerHeight] : [0, innerWidth],
    padding: 0.2,
  })
  const valueScale = scaleLinear<number>({
    domain: safeDomain,
    range: horizontal ? [0, innerWidth] : [innerHeight, 0],
    nice: true,
  })
  const seriesScale = scaleBand<string>({
    domain: plan.valueDataKeys,
    range: [0, categoryScale.bandwidth()],
    padding: 0.08,
  })
  const marks: VisxBarMark[] = []

  if (plan.kind !== 'line') {
    plan.data.forEach((row, rowIndex) => {
      const category = categories[rowIndex]
      const categoryPosition = categoryScale(category)
      if (categoryPosition === undefined) return
      let positive = 0
      let negative = 0
      plan.valueDataKeys.forEach((key, seriesIndex) => {
        const value = numericPlanValue(row, key)
        if (value === undefined) return
        const stackedStart = value >= 0 ? positive : negative
        const stackedEnd = stackedStart + value
        if (isStacked) {
          if (value >= 0) positive = stackedEnd
          else negative = stackedEnd
        }
        const start = isStacked ? stackedStart : 0
        const end = isStacked ? stackedEnd : value
        const color = chartColors[seriesIndex % chartColors.length]
        if (horizontal) {
          const x0 = valueScale(Math.min(start, end))
          const x1 = valueScale(Math.max(start, end))
          marks.push({
            id: `${category}-${key}`,
            rowIndex,
            seriesKey: key,
            x: x0,
            y: categoryPosition + (isStacked ? 0 : (seriesScale(key) ?? 0)),
            width: Math.max(x1 - x0, 0),
            height: isStacked
              ? categoryScale.bandwidth()
              : seriesScale.bandwidth(),
            color,
          })
        } else {
          const y0 = valueScale(Math.max(start, end))
          const y1 = valueScale(Math.min(start, end))
          marks.push({
            id: `${category}-${key}`,
            rowIndex,
            seriesKey: key,
            x: categoryPosition + (isStacked ? 0 : (seriesScale(key) ?? 0)),
            y: y0,
            width: isStacked
              ? categoryScale.bandwidth()
              : seriesScale.bandwidth(),
            height: Math.max(y1 - y0, 0),
            color,
          })
        }
      })
    })
  }

  return (
    <>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        data-visx-renderer="cartesian"
      >
        <rect
          x={margin.left}
          y={margin.top}
          width={innerWidth}
          height={innerHeight}
          fill="transparent"
          data-visx-plot-area
        />
        <Group top={margin.top} left={margin.left}>
          {plan.kind === 'line'
            ? plan.valueDataKeys.map((key, index) => {
                const seriesData = plan.data
                  .map((row, rowIndex) => ({ row, rowIndex }))
                  .filter(({ row }) => numericPlanValue(row, key) !== undefined)
                return (
                  <React.Fragment key={key}>
                    <LinePath<{ row: ReportingRenderRow; rowIndex: number }>
                      data={seriesData}
                      x={({ row }) =>
                        (categoryScale(
                          String(row[plan.categoryDataKey] ?? 'null'),
                        ) ?? 0) +
                        categoryScale.bandwidth() / 2
                      }
                      y={({ row }) =>
                        valueScale(numericPlanValue(row, key) ?? 0)
                      }
                      stroke={chartColors[index % chartColors.length]}
                      strokeWidth={2}
                      data-visx-mark="line"
                    />
                    {seriesData.map(({ row, rowIndex }) => {
                      const category = String(
                        row[plan.categoryDataKey] ?? 'null',
                      )
                      const value = numericPlanValue(row, key)
                      return (
                        <circle
                          key={`${key}-${rowIndex}`}
                          cx={
                            (categoryScale(category) ?? 0) +
                            categoryScale.bandwidth() / 2
                          }
                          cy={valueScale(value ?? 0)}
                          r={10}
                          fill="transparent"
                          data-visx-mark="line-point"
                          tabIndex={0}
                          role="graphics-symbol"
                          aria-label={`${formatDate(category, locale)}, ${
                            plan.seriesLabels[key] ?? translate('Value')
                          }: ${formatReportingValue(
                            value,
                            plan.valueFormat,
                            plan.valueCurrency,
                            locale,
                          )}`}
                          aria-describedby={
                            tooltip.active?.rowIndex === rowIndex &&
                            tooltip.active.seriesKey === key
                              ? tooltip.tooltipId
                              : undefined
                          }
                          css={[tooltipStyle.barSegment, interactiveLinePoint]}
                          onMouseEnter={(event) =>
                            tooltip.openFromPointer(rowIndex, key, event)
                          }
                          onMouseMove={(event) =>
                            tooltip.openFromPointer(rowIndex, key, event)
                          }
                          onMouseLeave={tooltip.scheduleClose}
                          onFocus={(event) =>
                            tooltip.openFromFocus(rowIndex, key, event)
                          }
                          onBlur={tooltip.close}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') tooltip.close()
                          }}
                        />
                      )
                    })}
                  </React.Fragment>
                )
              })
            : marks.map((mark) => (
                <Bar
                  key={mark.id}
                  x={mark.x}
                  y={mark.y}
                  width={mark.width}
                  height={mark.height}
                  fill={mark.color}
                  data-visx-mark="bar"
                  tabIndex={0}
                  role="graphics-symbol"
                  aria-label={`${categories[mark.rowIndex]}, ${
                    plan.seriesLabels[mark.seriesKey] ?? translate('Value')
                  }: ${formatReportingValue(
                    numericPlanValue(plan.data[mark.rowIndex], mark.seriesKey),
                    plan.valueFormat,
                    plan.valueCurrency,
                    locale,
                  )}`}
                  aria-describedby={
                    tooltip.active?.rowIndex === mark.rowIndex &&
                    tooltip.active.seriesKey === mark.seriesKey
                      ? tooltip.tooltipId
                      : undefined
                  }
                  css={tooltipStyle.barSegment}
                  onMouseEnter={(event) =>
                    tooltip.openFromPointer(
                      mark.rowIndex,
                      mark.seriesKey,
                      event,
                    )
                  }
                  onMouseMove={(event) =>
                    tooltip.openFromPointer(
                      mark.rowIndex,
                      mark.seriesKey,
                      event,
                    )
                  }
                  onMouseLeave={tooltip.scheduleClose}
                  onFocus={(event) =>
                    tooltip.openFromFocus(mark.rowIndex, mark.seriesKey, event)
                  }
                  onBlur={tooltip.close}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') tooltip.close()
                  }}
                />
              ))}
          {horizontal ? (
            <>
              <AxisBottom
                top={innerHeight}
                scale={valueScale}
                tickFormat={(value) =>
                  formatReportingValue(
                    Number(value),
                    plan.valueFormat,
                    plan.valueCurrency,
                    locale,
                  )
                }
                stroke={base_colorContentBorder}
                tickLabelProps={{ fill: text_colorTextSecondary, fontSize: 10 }}
              />
              <AxisLeft
                scale={categoryScale}
                stroke={base_colorContentBorder}
                tickLabelProps={{ fill: text_colorTextSecondary, fontSize: 10 }}
              />
            </>
          ) : (
            <>
              <AxisBottom
                top={innerHeight}
                scale={categoryScale}
                tickFormat={(value) =>
                  plan.kind === 'line'
                    ? formatDate(String(value), locale)
                    : String(value)
                }
                stroke={base_colorContentBorder}
                tickLabelProps={{ fill: text_colorTextSecondary, fontSize: 10 }}
              />
              <AxisLeft
                scale={valueScale}
                tickFormat={(value) =>
                  formatReportingValue(
                    Number(value),
                    plan.valueFormat,
                    plan.valueCurrency,
                    locale,
                  )
                }
                stroke={base_colorContentBorder}
                tickLabelProps={{ fill: text_colorTextSecondary, fontSize: 10 }}
              />
            </>
          )}
        </Group>
      </svg>
      {tooltip.renderedTooltip}
    </>
  )
}

export const ReportingVisxChart: React.FC<{
  plan: ReportingChartPlan
  locale: string
}> = ({ plan, locale }) =>
  plan.kind === 'pie' ? (
    <VisxPieChart plan={plan} locale={locale} />
  ) : (
    <VisxCartesianChart plan={plan} locale={locale} />
  )
